import React from 'react';
import { 
  CheckBadgeIcon, 
  ShieldCheckIcon, 
  UserGroupIcon,
  BeakerIcon,
  SparklesIcon,
  HeartIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';

const HomeService = () => {
    const services = [
        {
            title: 'บริการฟอกเลือดด้วยเครื่องไตเทียม แบบธรรมดา (HD)',
            shortTitle: 'Hemodialysis (HD)',
            description: 'เป็นวิธีการฟอกเลือดมาตรฐานที่ใช้ในผู้ป่วยไตวายเฉียบพลันและเรื้อรัง อาศัยหลักการแพร่ (Diffusion) เพื่อขจัดของเสียโมเลกุลขนาดเล็กและน้ำส่วนเกินอย่างมีประสิทธิภาพ',
            icon: BeakerIcon,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            gradient: 'from-blue-500 to-blue-600'
        },
        {
            title: 'บริการฟอกเลือดด้วยเครื่องไตเทียม แบบออนไลน์ (HDF)',
            shortTitle: 'Hemodiafiltration (HDF)',
            description: 'นวัตกรรมการฟอกเลือดที่ใช้ทั้งหลักการแพร่และการพา (Convection) โดยใช้สารน้ำบริสุทธิ์สูง (Sterile Dialysis Fluid) เพื่อการขจัดของเสียที่ครอบคลุมมากยิ่งขึ้น',
            icon: ShieldCheckIcon,
            color: 'text-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            gradient: 'from-orange-500 to-orange-600'
        },
    ];

    const stats = [
        { label: 'ศูนย์ไตเทียมทั่วประเทศ', value: '70+', icon: BuildingOffice2Icon, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { label: 'จำนวนเตียงรองรับ', value: '800+', icon: UserGroupIcon, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
        { label: 'ดูแลผู้ป่วยแล้วกว่า', value: '5,000+', icon: HeartIcon, color: 'text-rose-600', bgColor: 'bg-rose-50' },
    ];

    const certifications = [
        { 
            title: 'มาตรฐานระดับประเทศ', 
            description: 'รับรองมาตรฐานโดยสมาคมโรคไต และ HA',
            icon: CheckBadgeIcon,
            color: 'text-emerald-600',
            bgColor: 'bg-emerald-50',
            borderColor: 'border-emerald-200'
        },
        { 
            title: 'บริการครบวงจร', 
            description: 'ครอบคลุมทั้งในโรงพยาบาลและคลินิก',
            icon: SparklesIcon,
            color: 'text-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200'
        },
    ];

    const SERVICE_PHOTO = "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=1200";

    return (
        <section id="services" className="relative py-20 lg:py-28 bg-white overflow-hidden">
            {/* Decorative Background */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-100 rounded-full blur-3xl"></div>
            </div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                    
                    {/* ฝั่งซ้าย: เนื้อหาและรายละเอียด */}
                    <div className="w-full lg:w-1/2 order-2 lg:order-1">
                        {/* Header Section - Enhanced */}
                        <div className="mb-10 text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-xs font-black tracking-[0.2em] text-blue-600 uppercase bg-gradient-to-r from-blue-100 to-blue-50 rounded-full border border-blue-200 shadow-sm">
                                <SparklesIcon className="w-4 h-4" />
                                Our Services
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
                                ความพร้อมในการ <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-blue-600 to-orange-600">
                                    ให้บริการทางการแพทย์
                                </span>
                            </h2>
                            <div className="w-24 h-1.5 bg-gradient-to-r from-blue-600 to-orange-600 rounded-full mb-6 mx-auto lg:mx-0"></div>
                            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
                                ทีมพยาบาลและเจ้าหน้าที่ผู้เชี่ยวชาญกว่า <span className="font-bold text-blue-900">400 คน</span> ผ่านการรับรองจาก
                                <span className="font-bold text-orange-600"> สมาคมโรคไตแห่งประเทศไทย</span> พร้อมด้วยประสบการณ์ดูแลผู้ป่วยไตเทียมกว่า 
                                <span className="font-bold text-blue-900"> 20 ปี</span> ภายใต้มาตรฐาน HA และกองประกอบโรคศิลปะ
                            </p>
                        </div>

                        {/* Service Cards - Enhanced Design */}
                        <div className="space-y-5 mb-10">
                            {services.map((item, idx) => (
                                <div 
                                    key={idx} 
                                    className={`group relative flex flex-col sm:flex-row gap-5 p-6 rounded-3xl border-2 ${item.borderColor} bg-white hover:bg-gradient-to-br hover:from-white hover:to-slate-50 hover:shadow-2xl hover:shadow-blue-900/10 transition-all duration-500 transform hover:-translate-y-1`}
                                >
                                    {/* Icon */}
                                    <div className={`flex-shrink-0 w-16 h-16 rounded-2xl ${item.bgColor} ${item.color} flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                                        <item.icon className="w-8 h-8" strokeWidth={2} />
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h3 className="font-black text-slate-900 text-lg mb-1">{item.title}</h3>
                                                <p className={`text-xs font-bold ${item.color} uppercase tracking-wider`}>{item.shortTitle}</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                                    </div>

                                    {/* Decorative Corner */}
                                    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${item.gradient} rounded-bl-full opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
                                </div>
                            ))}
                        </div>

                        {/* Certifications - Enhanced */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {certifications.map((cert, idx) => (
                                <div 
                                    key={idx}
                                    className={`flex items-start gap-4 p-5 ${cert.bgColor} rounded-2xl border-2 ${cert.borderColor} hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
                                >
                                    <cert.icon className={`w-7 h-7 ${cert.color} mt-0.5 flex-shrink-0`} strokeWidth={2} />
                                    <div>
                                        <p className="font-black text-slate-900 text-sm mb-1">{cert.title}</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">{cert.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ฝั่งขวา: รูปภาพและสถิติ - Enhanced */}
                    <div className="w-full lg:w-1/2 relative order-1 lg:order-2">
                        <div className="relative z-10 rounded-3xl lg:rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white shadow-blue-900/20 transform rotate-0 lg:rotate-2 hover:rotate-0 transition-all duration-700 group">
                            <img 
                                src={SERVICE_PHOTO} 
                                alt="บริการฟอกเลือดด้วยเครื่องไตเทียม มาตรฐาน HA" 
                                className="w-full h-[400px] sm:h-[500px] lg:h-[650px] object-cover group-hover:scale-110 transition-transform duration-1000"
                                loading="lazy"
                            />
                            
                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/40 to-transparent"></div>
                            
                            {/* Stats Cards - Enhanced Design */}
                            <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-3">
                                {stats.map((stat, i) => (
                                    <div 
                                        key={i} 
                                        className={`${stat.bgColor} backdrop-blur-xl p-4 rounded-2xl text-center shadow-2xl border-2 border-white/50 transform hover:-translate-y-2 transition-all duration-300`}
                                    >
                                        <div className="flex justify-center mb-2">
                                            <div className={`p-2 rounded-xl ${stat.bgColor} ${stat.color}`}>
                                                <stat.icon className="w-5 h-5" strokeWidth={2.5} />
                                            </div>
                                        </div>
                                        <div className="text-2xl font-black text-slate-900 mb-1">{stat.value}</div>
                                        <div className="text-[9px] font-bold text-slate-600 uppercase tracking-wider leading-tight">{stat.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Trust Badge */}
                            <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-md px-5 py-3 rounded-2xl shadow-xl border-2 border-blue-100">
                                <div className="flex items-center gap-2">
                                    <CheckBadgeIcon className="w-6 h-6 text-blue-600" />
                                    <div>
                                        <p className="text-xs font-black text-blue-900 uppercase tracking-wider">HA Certified</p>
                                        <p className="text-[9px] font-bold text-slate-500">มาตรฐานสากล</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Decorative Elements */}
                        <div className="absolute -top-8 -right-8 w-64 h-64 bg-gradient-to-br from-blue-200 to-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse"></div>
                        <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-gradient-to-br from-orange-200 to-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>
                    </div>

                </div>
            </div>

            {/* SEO Content (Hidden but crawlable) */}
            <div className="sr-only">
                <h2>บริการฟอกเลือดด้วยเครื่องไตเทียม ราชพฤกษ์</h2>
                <p>
                    บริการฟอกเลือด HD และ HDF มาตรฐาน HA ด้วยทีมแพทย์พยาบาลผู้เชี่ยวชาญ 
                    ศูนย์ไตเทียมกว่า 70 แห่งทั่วประเทศไทย เตียงรองรับ 800+ เตียง 
                    ดูแลผู้ป่วยโรคไตด้วยเทคโนโลยีทันสมัยและการดูแลแบบองค์รวม
                </p>
            </div>
        </section>
    );
};

export default HomeService;
